import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ArrowUpRight, ChevronRight, Menu, X } from 'lucide-react';

import { Button } from '@/shared/components/ui';
import ThemeToggle from '@/shared/components/ThemeToggle';
import ComingSoonDialog from '@/shared/components/ComingSoonDialog';
import { cn } from '@/shared/utils/helpers';
import { contactDetails, siteNavigation } from '@/shared/utils/SolarData';

type SiteLayoutProps = {
  children: React.ReactNode;
};

const navClassName = ({ isActive }: { isActive: boolean }) =>
  cn(
    'rounded-full px-3 py-2 text-sm font-medium transition-colors',
    isActive
      ? 'bg-accent-primary/10 text-accent-primary'
      : 'text-text-secondary hover:bg-background-secondary hover:text-text-primary'
  );

export const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  return null;
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-border-primary/70 bg-background-overlay backdrop-blur-xl">
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center">
          <img
            src="/psmainlogo.png"
            alt="PS Group Solar Energy"
            className="h-14 w-14 rounded-full object-cover sm:h-16 sm:w-16"
          />
        </Link>

        <nav className="hidden items-center gap-1 xl:flex">
          {siteNavigation.map(item =>
            item.href === '/contact' ? (
              <button
                key={item.href}
                onClick={() => setDialogOpen(true)}
                className={cn(
                  'rounded-full px-3 py-2 text-sm font-medium transition-colors',
                  'text-text-secondary hover:bg-background-secondary hover:text-text-primary'
                )}
              >
                {item.label}
              </button>
            ) : (
              <NavLink key={item.href} to={item.href} className={navClassName}>
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden items-center gap-3 xl:flex">
          <ThemeToggle />
          <Button size="sm" className="shadow-lg shadow-accent-primary/15" onClick={() => setDialogOpen(true)}>
            Request Quote
            <ArrowUpRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2 xl:hidden">
          <ThemeToggle />
          <Button
            variant="outline"
            size="icon"
            onClick={() => setIsOpen(current => !current)}
            aria-label="Toggle navigation menu"
          >
            {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen ? (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2 }}
            className="border-t border-border-primary/70 bg-background-overlay xl:hidden"
          >
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-2 px-4 py-4 sm:px-6">
              {siteNavigation.map(item =>
                item.href === '/contact' ? (
                  <button
                    key={item.href}
                    onClick={() => { setIsOpen(false); setDialogOpen(true); }}
                    className="flex items-center justify-between rounded-xl px-4 py-3 text-sm font-medium transition-colors text-text-secondary hover:bg-background-secondary hover:text-text-primary w-full"
                  >
                    {item.label}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center justify-between rounded-xl px-4 py-3 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-accent-primary/10 text-accent-primary'
                          : 'text-text-secondary hover:bg-background-secondary hover:text-text-primary'
                      )
                    }
                  >
                    {item.label}
                    <ChevronRight className="h-4 w-4" />
                  </NavLink>
                )
              )}

              <Button className="mt-2 w-full" onClick={() => { setIsOpen(false); setDialogOpen(true); }}>
                Request Quote
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <ComingSoonDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </header>
  );
};

const Footer = () => {
  return (
    <footer className="border-t border-border-primary/70 bg-background-secondary/80">
      <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1.2fr_1fr_1fr] lg:px-8">
        <div className="space-y-5">
          <Link to="/" className="inline-flex items-center">
            <img
              src="/pslogofooter.png"
              alt="PS Group Solar Energy"
              className="h-28 w-auto sm:h-36"
            />
          </Link>
          <p className="max-w-md text-sm leading-6 text-text-secondary">
            Professional solar energy solutions for homes, businesses, and industries with a focus on quality, savings, and long-term value.
          </p>
        </div>

        <div>
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-text-primary">
            Quick Links
          </h3>
          <div className="grid gap-3">
            {siteNavigation.slice(0, 5).map(item => (
              <NavLink
                key={item.href}
                to={item.href}
                className="text-sm text-text-secondary transition-colors hover:text-accent-primary"
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>

        <div>
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-text-primary">
            Contact
          </h3>
          <div className="grid gap-3 text-sm text-text-secondary">
            <div>
              <p className="font-medium text-text-primary">Address</p>
              <p>{contactDetails.address}</p>
            </div>
            <div>
              <p className="font-medium text-text-primary">Phone</p>
              <p>{contactDetails.phone}</p>
            </div>
            <div>
              <p className="font-medium text-text-primary">Email</p>
              <p>{contactDetails.email}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border-primary/70">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-3 px-4 py-4 text-sm text-text-tertiary sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <p>© 2026 PS Group Solar Energy. All rights reserved.</p>
          <p>Designed for premium solar delivery, service, and scale.</p>
        </div>
      </div>
    </footer>
  );
};

export const SiteLayout = ({ children }: SiteLayoutProps) => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-background-primary text-text-primary">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[42rem] bg-[radial-gradient(circle_at_top_left,rgba(47,125,50,0.16),transparent_30%),radial-gradient(circle_at_top_right,rgba(244,180,0,0.16),transparent_28%),linear-gradient(180deg,rgba(30,78,121,0.06),transparent_40%)]" />
      <Navbar />
      <main className="relative">{children}</main>
      <Footer />
    </div>
  );
};
